
import { Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CreditCard } from "lucide-react";

interface StudentActivitiesConfigProps {
  activities: any[];
  activitiesLoading: boolean;
  onDayChange: (activityId: string, day: number, checked: boolean) => void;
  onBillingTypeChange: (activityId: string, billingType: string) => void;
  getEffectiveBillingType: (activity: any) => string;
  getBillingTypeDisplayText: (billingType: 'per_session' | 'monthly') => string;
  renderActivitySchedule: (activity: any) => JSX.Element;
}

export const StudentActivitiesConfig = ({
  activities,
  activitiesLoading,
  onDayChange,
  onBillingTypeChange,
  getEffectiveBillingType,
  getBillingTypeDisplayText,
  renderActivitySchedule
}: StudentActivitiesConfigProps) => {
  if (activitiesLoading) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        Cargando actividades...
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No hay actividades inscritas
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => (
        <Card key={activity.id} className="p-4">
          <CardHeader className="p-0 pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              {activity.activityName}
              <div className="flex gap-2">
                <Badge variant="outline" className="text-xs">
                  {getBillingTypeDisplayText(getEffectiveBillingType(activity) as 'per_session' | 'monthly')}
                </Badge>
                <Badge variant={activity.status === 'active' ? 'default' : 'secondary'}>
                  {activity.status === 'active' ? 'Activa' : 'Inactiva'}
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4">
            {/* Configuración de tipo de facturación */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <Label className="text-sm font-medium">Tipo de facturación</Label>
              </div>
              <Select
                value={activity.billingTypeOverride || 'default'}
                onValueChange={(value) => onBillingTypeChange(activity.activityId, value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    Por defecto ({activity.availableBillingTypes[0] ? getBillingTypeDisplayText(activity.availableBillingTypes[0]) : 'Sin configurar'})
                  </SelectItem>
                  {activity.availableBillingTypes.map((billingType) => (
                    <SelectItem key={billingType} value={billingType}>
                      {getBillingTypeDisplayText(billingType)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activity.billingTypeOverride && (
                <p className="text-xs text-muted-foreground">
                  ⚠️ Configuración personalizada activa. Por defecto: {activity.availableBillingTypes[0] ? getBillingTypeDisplayText(activity.availableBillingTypes[0]) : 'Sin configurar'}
                </p>
              )}
            </div>
            
            <Separator />
            
            {/* Configuración de horarios */}
            {renderActivitySchedule(activity)}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
